// @ts-nocheck — Pre-existing type errors (stale code drifted from refactored types / WIP).
// Suppressed to unblock the admin build; runtime unchanged. TODO(owner): restore type-safety.
import { apiClient } from '@/lib/api/client'
import { env } from '@/config/env'

export interface Invoice {
  id: number
  invoiceNumber: string
  type: 'artist_sales' | 'order'
  status: 'draft' | 'issued' | 'paid' | 'cancelled'
  artistId: number | null
  shopifyOrderId: string | null
  periodStart: string | null
  periodEnd: string | null
  subtotal: number
  taxAmount: number
  totalAmount: number
  currency: string
  customerName: string | null
  customerEmail: string | null
  customerAddress: string | null
  notes: string | null
  pdfPath: string | null
  issuedAt: string | null
  dueAt: string | null
  createdAt: string
  updatedAt: string
}

export interface InvoiceItem {
  id: number
  invoiceId: number
  description: string
  sku: string | null
  shopifyOrderId: string | null
  artworkId: number | null
  artworkName: string | null
  quantity: number
  unitPrice: number
  commissionRate: number | null
  commissionAmount: number
  lineTotal: number
  orderDate: string | null
  orderStatus: string | null
  createdAt: string
}

export interface InvoiceWithItems {
  invoice: Invoice
  items: InvoiceItem[]
  artist: {
    id: number
    firstName: string
    lastName: string
    email: string
    artisticName: string | null
    paypal: string | null
  } | null
}

export interface MonthlySummary {
  month: string
  monthLabel: string
  periodStart: string
  periodEnd: string
  salesCount: number
  totalSales: number
  totalCommission: number
}

export interface CreateArtistInvoiceParams {
  artistId: number
  periodStart: string
  periodEnd: string
  notes?: string
}

export interface CreateOrderInvoiceParams {
  shopifyOrderId: string
  notes?: string
}

export interface InvoiceListParams {
  start?: number
  limit?: number
  type?: string
  status?: string
  minDate?: string
  maxDate?: string
}

export const invoicesApi = {
  /**
   * Get all invoices (paginated)
   */
  getInvoices: async (params: InvoiceListParams = {}) => {
    const response = await apiClient.get<Invoice[]>('/api/invoices', {
      params: {
        start: params.start || 0,
        limit: params.limit || 20,
        type: params.type,
        status: params.status,
        minDate: params.minDate,
        maxDate: params.maxDate,
      },
    })
    const total = parseInt(response.headers['count'] || '0', 10)
    return {
      data: response.data || [],
      total,
      start: params.start || 0,
      limit: params.limit || 20,
    }
  },

  /**
   * Get single invoice with items
   */
  getInvoice: async (id: number): Promise<InvoiceWithItems> => {
    const response = await apiClient.get<InvoiceWithItems>(`/api/invoices/${id}`)
    return response.data
  },

  /**
   * Get monthly summary for an artist
   */
  getMonthlySummary: async (artistId?: number): Promise<MonthlySummary[]> => {
    const response = await apiClient.get<MonthlySummary[]>('/api/invoices/monthly-summary', {
      params: artistId ? { artistId } : undefined,
    })
    return response.data
  },

  /**
   * Create artist sales invoice
   */
  createArtistSalesInvoice: async (params: CreateArtistInvoiceParams): Promise<InvoiceWithItems> => {
    const response = await apiClient.post<InvoiceWithItems>('/api/invoices/artist-sales', params)
    return response.data
  },

  /**
   * Create order invoice from Shopify order (Manager only)
   */
  createOrderInvoice: async (params: CreateOrderInvoiceParams): Promise<InvoiceWithItems> => {
    const response = await apiClient.post<InvoiceWithItems>('/api/invoices/order', params)
    return response.data
  },

  /**
   * Update invoice status
   */
  updateStatus: async (id: number, status: string): Promise<Invoice> => {
    const response = await apiClient.patch<Invoice>(`/api/invoices/${id}/status`, { status })
    return response.data
  },

  /**
   * Regenerate PDF for an invoice
   */
  regeneratePdf: async (id: number): Promise<Invoice> => {
    const response = await apiClient.post<Invoice>(`/api/invoices/${id}/regenerate-pdf`)
    return response.data
  },

  /**
   * Get download URL for invoice PDF
   */
  getDownloadUrl: async (id: number): Promise<string> => {
    const response = await apiClient.get<{ url: string }>(`/api/invoices/${id}/download`)
    return response.data.url
  },

  /**
   * Delete an invoice
   */
  deleteInvoice: async (id: number): Promise<void> => {
    await apiClient.delete(`/api/invoices/${id}`)
  },
}
